import React, { createContext, useContext, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectIsPortalPersona } from "../store/auth-slice";

interface LocationContextType {
  userLocation: [number, number] | null;
  loading: boolean;
  error: string | null;
}

const LocationContext = createContext<LocationContextType>({
  userLocation: null,
  loading: true,
  error: null,
});

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userLocation, setUserLocation] = useState<[number, number] | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // ITAM-2887: a portal persona (advisor, association) never needs the
  // device location; do not prompt.
  const isPortalPersona = useSelector(selectIsPortalPersona);

  useEffect(() => {
    if (isPortalPersona) {
      setLoading(false);
      return;
    }

    console.log(
      "[LocationProvider] Requesting user location (once for entire app)..."
    );

    if (!navigator.geolocation) {
      setError("Geolocation not supported");
      setUserLocation([44.7866, 20.4489]); // Belgrade fallback
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        console.log("[LocationProvider] Got location:", {
          latitude,
          longitude,
        });
        setUserLocation([latitude, longitude]);
        setError(null);
        setLoading(false);
      },
      (error) => {
        console.warn(
          "[LocationProvider] Geolocation failed:",
          error.message
        );
        setError(error.message);
        setUserLocation([44.7866, 20.4489]); // Belgrade fallback
        setLoading(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, [isPortalPersona]);

  return (
    <LocationContext.Provider value={{ userLocation, loading, error }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error("useLocation must be used within LocationProvider");
  }
  return context;
};
